package com.rainbowclubs.rainbow.presentation.events

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.pulltorefresh.PullToRefreshBox
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import com.rainbowclubs.rainbow.data.models.AttendeeStatus
import com.rainbowclubs.rainbow.data.models.Comment
import com.rainbowclubs.rainbow.data.models.Event
import com.rainbowclubs.rainbow.presentation.ScreenState
import com.rainbowclubs.rainbow.presentation.common.ErrorScreen
import com.rainbowclubs.rainbow.presentation.common.LoadingScreen
import com.rainbowclubs.rainbow.presentation.theme.RainbowClubTheme
import com.rainbowclubs.rainbow.presentation.theme.rainbowGradient
import com.rainbowclubs.rainbow.presentation.theme.transparent
import org.jetbrains.compose.resources.DrawableResource
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel
import org.koin.core.parameter.parametersOf
import rainbowclubs.composeapp.generated.resources.Res
import rainbowclubs.composeapp.generated.resources.add_comment_placeholder
import rainbowclubs.composeapp.generated.resources.attendee_count
import rainbowclubs.composeapp.generated.resources.back_icon_cd
import rainbowclubs.composeapp.generated.resources.calendar_icon_cd
import rainbowclubs.composeapp.generated.resources.comments
import rainbowclubs.composeapp.generated.resources.event_details
import rainbowclubs.composeapp.generated.resources.general_error
import rainbowclubs.composeapp.generated.resources.going
import rainbowclubs.composeapp.generated.resources.ic_arrow_back
import rainbowclubs.composeapp.generated.resources.ic_calendar
import rainbowclubs.composeapp.generated.resources.ic_cancel
import rainbowclubs.composeapp.generated.resources.ic_check
import rainbowclubs.composeapp.generated.resources.ic_clock
import rainbowclubs.composeapp.generated.resources.ic_location
import rainbowclubs.composeapp.generated.resources.ic_question_mark
import rainbowclubs.composeapp.generated.resources.maybe
import rainbowclubs.composeapp.generated.resources.no_comments_title
import rainbowclubs.composeapp.generated.resources.not_going
import rainbowclubs.composeapp.generated.resources.now
import rainbowclubs.composeapp.generated.resources.send_comment
import rainbowclubs.composeapp.generated.resources.try_again_button
import rainbowclubs.composeapp.generated.resources.upcoming_event
import rainbowclubs.composeapp.generated.resources.your_rsvp

@Composable
fun EventDetailsScreen(
    eventId: String,
    viewModel: EventDetailsViewModel = koinViewModel(parameters = { parametersOf(eventId) }),
    navigateTo: (EventDetailsViewModel.NavigationEvent) -> Unit,
) {
    val screenState by viewModel.screenState.collectAsState()

    LaunchedEffect(Unit) {
        viewModel.navigationIntent.collect { intent ->
            navigateTo(intent)
        }
    }

    Scaffold(
        containerColor = MaterialTheme.colorScheme.transparent,
    ) { paddingValues ->
        Box(
            modifier = Modifier
                .padding(paddingValues)
                .fillMaxSize(),
        ) {
            when (val state = screenState) {
                is ScreenState.Loading -> LoadingScreen(Modifier.fillMaxSize())
                is ScreenState.Error -> ErrorScreen(
                    error = stringResource(Res.string.general_error),
                    onButtonClick = { viewModel.fetchEvent() },
                    buttonText = stringResource(Res.string.try_again_button),
                )

                is ScreenState.Content -> EventDetailsScreenContent(
                    uiState = state.data,
                    currentUserStatus = viewModel.currentUserStatus(state.data.event),
                    isLive = viewModel.isEventLive(state.data.event),
                    onBackClick = viewModel::navigateBack,
                    onRefresh = viewModel::refreshEvent,
                    onCommentChanged = viewModel::onCommentChanged,
                    onSendComment = viewModel::sendComment,
                    onRsvpClick = viewModel::updateRsvp,
                    formatDate = viewModel::formatDate,
                    formatTime = viewModel::formatTime,
                    formatCommentDateTime = viewModel::formatCommentDateTime,
                )
            }
        }
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun EventDetailsScreenContent(
    uiState: EventDetailsUiState,
    currentUserStatus: AttendeeStatus?,
    isLive: Boolean,
    onBackClick: () -> Unit,
    onRefresh: () -> Unit,
    onCommentChanged: (String) -> Unit,
    onSendComment: () -> Unit,
    onRsvpClick: (AttendeeStatus) -> Unit,
    formatDate: (Long) -> String,
    formatTime: (Long) -> String,
    formatCommentDateTime: (Long) -> String,
) {
    PullToRefreshBox(
        isRefreshing = uiState.isRefreshing,
        onRefresh = onRefresh,
        modifier = Modifier.fillMaxSize(),
    ) {
        Column(
            modifier = Modifier
                .fillMaxSize()
                .verticalScroll(rememberScrollState())
        ) {
            Spacer(modifier = Modifier.height(24.dp))

            EventDetailsHeader(onBackClick = onBackClick)

            Spacer(modifier = Modifier.height(28.dp))

            EventDetailsCard(
                event = uiState.event,
                isLive = isLive,
                currentUserStatus = currentUserStatus,
                rsvpLoadingStatus = uiState.rsvpLoadingStatus,
                onRsvpClick = onRsvpClick,
                formatDate = formatDate,
                formatTime = formatTime,
            )

            Spacer(modifier = Modifier.height(28.dp))

            Text(
                text = stringResource(Res.string.comments),
                style = MaterialTheme.typography.titleLarge,
                color = MaterialTheme.colorScheme.onBackground,
            )

            Spacer(modifier = Modifier.height(16.dp))

            CommentComposer(
                commentText = uiState.commentText,
                isSending = uiState.isSendingComment,
                onCommentChanged = onCommentChanged,
                onSendComment = onSendComment,
            )

            uiState.commentError?.let {
                Spacer(modifier = Modifier.height(8.dp))
                Text(
                    text = stringResource(it),
                    style = MaterialTheme.typography.titleMedium,
                    color = MaterialTheme.colorScheme.error,
                )
            }

            Spacer(modifier = Modifier.height(16.dp))

            CommentsList(
                comments = uiState.event.comments,
                formatCommentDateTime = formatCommentDateTime,
            )

            Spacer(modifier = Modifier.height(36.dp))
        }
    }
}

@Composable
private fun EventDetailsHeader(onBackClick: () -> Unit) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        IconButton(
            onClick = onBackClick,
            modifier = Modifier.size(32.dp),
        ) {
            Icon(
                painter = painterResource(Res.drawable.ic_arrow_back),
                contentDescription = stringResource(Res.string.back_icon_cd),
                modifier = Modifier.size(24.dp),
                tint = MaterialTheme.colorScheme.onBackground,
            )
        }

        Spacer(modifier = Modifier.width(12.dp))

        Text(
            text = stringResource(Res.string.event_details),
            style = MaterialTheme.typography.titleLarge,
            color = MaterialTheme.colorScheme.onBackground,
        )
    }
}

@Composable
private fun EventDetailsCard(
    event: Event,
    isLive: Boolean,
    currentUserStatus: AttendeeStatus?,
    rsvpLoadingStatus: AttendeeStatus?,
    onRsvpClick: (AttendeeStatus) -> Unit,
    formatDate: (Long) -> String,
    formatTime: (Long) -> String,
) {
    val goingCount = event.attendees.count { it.status == AttendeeStatus.YES }
    val maybeCount = event.attendees.count { it.status == AttendeeStatus.MAYBE }

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .background(
                color = MaterialTheme.colorScheme.surface,
                shape = MaterialTheme.shapes.large,
            )
            .border(
                width = 2.dp,
                brush = MaterialTheme.colorScheme.rainbowGradient,
                shape = MaterialTheme.shapes.large,
            )
            .padding(16.dp),
    ) {
        EventStatusLabel(isLive = isLive)

        Spacer(modifier = Modifier.height(16.dp))

        Text(
            text = event.title,
            style = MaterialTheme.typography.titleLarge,
            color = MaterialTheme.colorScheme.onSurface,
        )

        Spacer(modifier = Modifier.height(8.dp))

        Text(
            text = event.description,
            style = MaterialTheme.typography.titleMedium,
            color = MaterialTheme.colorScheme.outline,
        )

        Spacer(modifier = Modifier.height(20.dp))

        EventInfoRow(
            text = formatDate(event.startsAt),
            drawableResource = Res.drawable.ic_calendar,
            contentDescription = stringResource(Res.string.calendar_icon_cd),
        )
        EventInfoRow(
            text = "${formatTime(event.startsAt)} - ${formatTime(event.endsAt)}",
            drawableResource = Res.drawable.ic_clock,
            contentDescription = null,
        )
        event.location?.let {
            EventInfoRow(
                text = it,
                drawableResource = Res.drawable.ic_location,
                contentDescription = it,
            )
        }

        Spacer(modifier = Modifier.height(20.dp))

        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(12.dp),
        ) {
            AttendanceCountCard(
                count = goingCount,
                label = stringResource(Res.string.going),
                contentColor = MaterialTheme.colorScheme.secondary,
                containerColor = MaterialTheme.colorScheme.secondaryContainer,
                modifier = Modifier.weight(1f),
            )
            AttendanceCountCard(
                count = maybeCount,
                label = stringResource(Res.string.maybe),
                contentColor = MaterialTheme.colorScheme.tertiary,
                containerColor = MaterialTheme.colorScheme.tertiaryContainer,
                modifier = Modifier.weight(1f),
            )
        }

        Spacer(modifier = Modifier.height(20.dp))

        Text(
            text = stringResource(Res.string.your_rsvp),
            style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.Bold),
            color = MaterialTheme.colorScheme.onSurface,
        )

        Spacer(modifier = Modifier.height(12.dp))

        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(8.dp),
        ) {
            RsvpButton(
                text = stringResource(Res.string.going),
                drawableResource = Res.drawable.ic_check,
                status = AttendeeStatus.YES,
                isSelected = currentUserStatus == AttendeeStatus.YES,
                isLoading = rsvpLoadingStatus == AttendeeStatus.YES,
                onClick = { onRsvpClick(AttendeeStatus.YES) },
                modifier = Modifier.weight(1f),
            )
            RsvpButton(
                text = stringResource(Res.string.maybe),
                drawableResource = Res.drawable.ic_question_mark,
                status = AttendeeStatus.MAYBE,
                isSelected = currentUserStatus == AttendeeStatus.MAYBE,
                isLoading = rsvpLoadingStatus == AttendeeStatus.MAYBE,
                onClick = { onRsvpClick(AttendeeStatus.MAYBE) },
                modifier = Modifier.weight(1f),
            )
            RsvpButton(
                text = stringResource(Res.string.not_going),
                drawableResource = Res.drawable.ic_cancel,
                status = AttendeeStatus.NO,
                isSelected = currentUserStatus == AttendeeStatus.NO,
                isLoading = rsvpLoadingStatus == AttendeeStatus.NO,
                onClick = { onRsvpClick(AttendeeStatus.NO) },
                modifier = Modifier.weight(1f),
            )
        }
    }
}

@Composable
private fun EventStatusLabel(isLive: Boolean) {
    Row(verticalAlignment = Alignment.CenterVertically) {
        if (isLive) {
            Box(
                modifier = Modifier
                    .size(10.dp)
                    .background(MaterialTheme.colorScheme.error, CircleShape),
            )
        } else {
            Icon(
                painter = painterResource(Res.drawable.ic_calendar),
                contentDescription = stringResource(Res.string.calendar_icon_cd),
                modifier = Modifier.size(20.dp),
                tint = MaterialTheme.colorScheme.primary,
            )
        }
        Spacer(modifier = Modifier.width(8.dp))
        Text(
            text = stringResource(if (isLive) Res.string.now else Res.string.upcoming_event),
            color = MaterialTheme.colorScheme.primary,
            style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.Bold),
            maxLines = 1,
            overflow = TextOverflow.Ellipsis,
        )
    }
}

@Composable
private fun EventInfoRow(
    text: String,
    drawableResource: DrawableResource,
    contentDescription: String?,
) {
    Row(
        verticalAlignment = Alignment.CenterVertically,
        modifier = Modifier.padding(vertical = 4.dp),
    ) {
        Icon(
            painter = painterResource(drawableResource),
            contentDescription = contentDescription,
            modifier = Modifier.size(18.dp),
            tint = MaterialTheme.colorScheme.onSurface,
        )
        Spacer(modifier = Modifier.width(10.dp))
        Text(
            text = text,
            style = MaterialTheme.typography.titleMedium,
            color = MaterialTheme.colorScheme.outline,
        )
    }
}

@Composable
private fun AttendanceCountCard(
    count: Int,
    label: String,
    contentColor: androidx.compose.ui.graphics.Color,
    containerColor: androidx.compose.ui.graphics.Color,
    modifier: Modifier = Modifier,
) {
    Surface(
        modifier = modifier,
        shape = MaterialTheme.shapes.medium,
        color = containerColor.copy(alpha = 0.3f),
        border = BorderStroke(1.dp, contentColor.copy(alpha = 0.24f)),
    ) {
        Column(
            modifier = Modifier.padding(14.dp),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            Text(
                text = stringResource(Res.string.attendee_count, count),
                style = MaterialTheme.typography.titleLarge,
                color = contentColor,
            )
            Text(
                text = label,
                style = MaterialTheme.typography.titleMedium,
                color = MaterialTheme.colorScheme.outline,
            )
        }
    }
}

@Composable
private fun RsvpButton(
    text: String,
    drawableResource: DrawableResource,
    status: AttendeeStatus,
    isSelected: Boolean,
    isLoading: Boolean,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
) {
    val colorScheme = MaterialTheme.colorScheme
    val (contentColor, containerColor) = when (status) {
        AttendeeStatus.YES -> colorScheme.secondary to colorScheme.secondaryContainer
        AttendeeStatus.MAYBE -> colorScheme.tertiary to colorScheme.tertiaryContainer
        AttendeeStatus.NO -> colorScheme.error to colorScheme.errorContainer
    }
    val finalContainerColor =
        if (isSelected) containerColor.copy(alpha = 0.4f) else colorScheme.surface
    val finalContentColor = if (isSelected) contentColor else colorScheme.outline

    Surface(
        onClick = onClick,
        modifier = modifier,
        enabled = !isLoading,
        shape = MaterialTheme.shapes.medium,
        color = finalContainerColor,
        border = BorderStroke(1.dp, finalContentColor.copy(alpha = 0.28f)),
    ) {
        Row(
            modifier = Modifier.padding(horizontal = 6.dp, vertical = 10.dp),
            horizontalArrangement = Arrangement.Center,
            verticalAlignment = Alignment.CenterVertically,
        ) {
            if (isLoading) {
                CircularProgressIndicator(
                    modifier = Modifier.size(18.dp),
                    color = finalContentColor,
                    strokeWidth = 2.dp,
                )
            } else {
                Icon(
                    painter = painterResource(drawableResource),
                    contentDescription = text,
                    modifier = Modifier.size(18.dp),
                    tint = finalContentColor,
                )
            }
            Spacer(modifier = Modifier.width(4.dp))
            Text(
                text = text,
                style = MaterialTheme.typography.titleMedium,
                color = finalContentColor,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
            )
        }
    }
}

@Composable
private fun CommentComposer(
    commentText: String,
    isSending: Boolean,
    onCommentChanged: (String) -> Unit,
    onSendComment: () -> Unit,
) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.spacedBy(10.dp),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        OutlinedTextField(
            value = commentText,
            onValueChange = onCommentChanged,
            modifier = Modifier.weight(1f),
            placeholder = {
                Text(
                    text = stringResource(Res.string.add_comment_placeholder),
                    style = MaterialTheme.typography.titleMedium,
                )
            },
            textStyle = MaterialTheme.typography.titleMedium,
            minLines = 1,
            maxLines = 3,
            enabled = !isSending,
            shape = MaterialTheme.shapes.medium,
        )

        Button(
            onClick = onSendComment,
            enabled = !isSending && commentText.isNotBlank(),
            shape = MaterialTheme.shapes.medium,
            contentPadding = PaddingValues(horizontal = 18.dp, vertical = 14.dp),
            colors = ButtonDefaults.buttonColors(
                containerColor = MaterialTheme.colorScheme.primary,
                contentColor = MaterialTheme.colorScheme.onPrimary,
            ),
        ) {
            if (isSending) {
                CircularProgressIndicator(
                    modifier = Modifier.size(20.dp),
                    color = MaterialTheme.colorScheme.onPrimary,
                    strokeWidth = 2.dp,
                )
            } else {
                Text(
                    text = stringResource(Res.string.send_comment),
                    style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.Bold),
                )
            }
        }
    }
}

@Composable
private fun CommentsList(
    comments: List<Comment>,
    formatCommentDateTime: (Long) -> String,
) {
    if (comments.isEmpty()) {
        Surface(
            modifier = Modifier.fillMaxWidth(),
            shape = MaterialTheme.shapes.medium,
            color = MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.32f),
            border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
        ) {
            Text(
                text = stringResource(Res.string.no_comments_title),
                style = MaterialTheme.typography.titleMedium,
                color = MaterialTheme.colorScheme.outline,
                modifier = Modifier.padding(16.dp),
            )
        }
        return
    }

    Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
        comments.forEach { comment ->
            CommentCard(
                comment = comment,
                formattedDateTime = formatCommentDateTime(comment.createdAt),
            )
        }
    }
}

@Composable
private fun CommentCard(
    comment: Comment,
    formattedDateTime: String,
) {
    Surface(
        modifier = Modifier.fillMaxWidth(),
        shape = MaterialTheme.shapes.medium,
        color = MaterialTheme.colorScheme.surface,
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
    ) {
        Column(modifier = Modifier.padding(16.dp)) {
            Text(
                text = comment.userName,
                style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.Bold),
                color = MaterialTheme.colorScheme.onSurface,
            )
            Spacer(modifier = Modifier.height(6.dp))
            Text(
                text = comment.commentText,
                style = MaterialTheme.typography.bodyLarge,
                color = MaterialTheme.colorScheme.onSurface,
            )
            Spacer(modifier = Modifier.height(8.dp))
            Text(
                text = formattedDateTime,
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.outline,
            )
        }
    }
}

@Preview
@Composable
private fun EventDetailsScreenContentPreview() {
    val event = Event(
        id = "event-1",
        clubId = "club-1",
        startsAt = 1780412400000L,
        endsAt = 1780428600000L,
        description = "Event description",
        title = "now meeting",
        location = "Here",
        attendees = listOf(
            com.rainbowclubs.rainbow.data.models.Attendee("1", AttendeeStatus.YES),
            com.rainbowclubs.rainbow.data.models.Attendee("2", AttendeeStatus.YES),
            com.rainbowclubs.rainbow.data.models.Attendee("3", AttendeeStatus.MAYBE),
        ),
        comments = listOf(
            Comment("This sounds great!", 1780409520000L, "1", "test"),
            Comment("I might arrive late.", 1780410300000L, "2", "Sarah"),
        ),
    )

    RainbowClubTheme {
        EventDetailsScreenContent(
            uiState = EventDetailsUiState(event = event),
            currentUserStatus = AttendeeStatus.YES,
            isLive = true,
            onBackClick = {},
            onRefresh = {},
            onCommentChanged = {},
            onSendComment = {},
            onRsvpClick = {},
            formatDate = { "Tue, Jun 02 2026" },
            formatTime = { "5:00 PM" },
            formatCommentDateTime = { "Tue, Jun 02 2026 at 4:12 PM" },
        )
    }
}
